(function () {
  const GRANTED = 'granted';
  const DENIED = 'denied';
  const MIRROR_COOKIE_NAME = 'vecicky_gtm_consent';
  const LEGACY_COOKIE_NAME = 'eshop-cookies-accepted';
  const LEGACY_ANALYTICS_CATEGORY = 2;
  const LEGACY_PERSONALIZATION_CATEGORY = 3;
  const LEGACY_MARKETING_CATEGORY = 4;
  const MIRROR_COOKIE_MAX_AGE = 365 * 24 * 60 * 60;
  const SHOPIFY_INIT_MAX_ATTEMPTS = 50;
  const SHOPIFY_INIT_RETRY_MS = 100;

  let lastAppliedConsentKey = null;
  // Opt-in only: enable together with the matching Consent Initialization
  // template in a preview first. Existing deployments keep their legacy path.
  const nativeConsentEnabled = window.VecickyGtmNativeConsent === true;
  let nativeConsentCallback = null;
  let latestConsent = null;
  let exitWaitReleased = false;

  function releasePendingConsentOnExit() {
    // Explicit kill switch. Exit release is best-effort only; the approved
    // 100ms link delay is not enough to drain every Google event batch.
    if (window.VecickyGtmExitFlush === false) return;
    if (exitWaitReleased || (latestConsent && latestConsent.analytics_storage)) {
      return;
    }
    // End the pending wait under the existing denied defaults, NOT a visitor
    // refusal. Never persist this or merge it into confirmed choice state.
    const state = Object.assign({}, buildConsentState(false, false), latestConsent || {});
    if (nativeConsentEnabled) {
      if (!nativeConsentCallback) return;
      nativeConsentCallback(state);
    } else if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', state);
    } else {
      return;
    }
    exitWaitReleased = true;
  }

  window.addEventListener('pagehide', releasePendingConsentOnExit);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') releasePendingConsentOnExit();
  });

  // A normal primary-button link navigation gives the queued Google update
  // one bounded task window before replacing this document. Do not intercept
  // modified/new-tab/download/hash/JavaScript or already handled clicks.
  document.addEventListener('click', function (event) {
    if (window.VecickyGtmExitFlush === false) return;
    if (event.defaultPrevented || event.button !== 0 || event.ctrlKey ||
        event.metaKey || event.shiftKey || event.altKey ||
        exitWaitReleased || (latestConsent && latestConsent.analytics_storage)) return;
    const link = event.target && event.target.closest && event.target.closest('a[href]');
    if (!link || link.hasAttribute('download') ||
        (link.target && link.target !== '_self')) return;
    const url = new URL(link.href, window.location.href);
    if (!/^https?:$/.test(url.protocol) ||
        url.origin !== window.location.origin ||
        (url.pathname === window.location.pathname && url.search === window.location.search)) return;
    event.preventDefault();
    releasePendingConsentOnExit();
    window.setTimeout(function () { window.location.assign(url.href); }, 100);
  });

  function registerNativeConsent(callback) {
    if (!nativeConsentEnabled || typeof callback !== 'function') {
      return false;
    }

    nativeConsentCallback = callback;
    if (latestConsent) {
      callback(Object.assign({}, latestConsent));
    }
    return true;
  }

  function buildConsentState(analyticsAllowed, marketingAllowed) {
    const state = { functionality_storage: GRANTED };
    if (typeof analyticsAllowed === 'boolean') {
      state.analytics_storage = analyticsAllowed ? GRANTED : DENIED;
    }
    if (typeof marketingAllowed === 'boolean') {
      const value = marketingAllowed ? GRANTED : DENIED;
      state.personalization_storage = value;
      state.ad_storage = value;
      state.ad_user_data = value;
      state.ad_personalization = value;
    }
    return state;
  }

  function consentStateKey(consent) {
    return [
      consent.analytics_storage,
      consent.personalization_storage,
      consent.ad_storage,
      consent.ad_user_data,
      consent.ad_personalization,
    ].join('|');
  }

  function readCookieValue(cookieName) {
    const prefix = cookieName + '=';
    const parts = document.cookie.split(';');

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i].trim();
      if (part.indexOf(prefix) === 0) {
        return part.substring(prefix.length);
      }
    }

    return null;
  }

  function readMirrorCookie() {
    const raw = readCookieValue(MIRROR_COOKIE_NAME);
    if (raw == null) {
      return null;
    }

    try {
      return JSON.parse(decodeURIComponent(raw));
    } catch (error) {
      return null;
    }
  }

  function writeMirrorCookie(analyticsAllowed, marketingAllowed) {
    const payload = JSON.stringify({
      analytics: analyticsAllowed,
      marketing: marketingAllowed,
      choice_confirmed: true,
    });

    let cookie =
      MIRROR_COOKIE_NAME +
      '=' +
      encodeURIComponent(payload) +
      '; path=/' +
      '; max-age=' +
      MIRROR_COOKIE_MAX_AGE +
      '; SameSite=Lax';

    if (window.location.protocol === 'https:') {
      cookie += '; Secure';
    }

    document.cookie = cookie;
  }

  function legacyCategoryAccepted(categories, categoryId) {
    for (let i = 0; i < categories.length; i++) {
      if (Number(categories[i]) === categoryId) {
        return true;
      }
    }
    return false;
  }

  /**
   * Legacy 3IT cookie eshop-cookies-accepted: JSON array of category IDs.
   * 2 = statistics, 3 = personalization, 4 = marketing/ad.
   */
  function readLegacyCookie() {
    const raw = readCookieValue(LEGACY_COOKIE_NAME);
    if (raw == null) {
      return null;
    }

    try {
      const parsed = JSON.parse(decodeURIComponent(raw));
      return Array.isArray(parsed) ? parsed : null;
    } catch (error) {
      try {
        const parsed = JSON.parse(decodeURI(raw));
        return Array.isArray(parsed) ? parsed : null;
      } catch (innerError) {
        return null;
      }
    }
  }

  function consentFromLegacyCategories(categories) {
    const analyticsAllowed = legacyCategoryAccepted(
      categories,
      LEGACY_ANALYTICS_CATEGORY
    );
    const marketingAllowed =
      legacyCategoryAccepted(categories, LEGACY_PERSONALIZATION_CATEGORY) ||
      legacyCategoryAccepted(categories, LEGACY_MARKETING_CATEGORY);

    return buildConsentState(analyticsAllowed, marketingAllowed);
  }

  function applyGoogleConsentState(consent) {
    const mergedConsent = Object.assign({}, latestConsent || {}, consent);
    const key = consentStateKey(mergedConsent);
    if (key === lastAppliedConsentKey) {
      return false;
    }

    latestConsent = mergedConsent;
    if (nativeConsentEnabled) {
      // The GTM sandbox callback uses updateConsentState directly. Do not mix
      // in queued gtag updates: a stale update could overtake a later choice.
      if (nativeConsentCallback) {
        nativeConsentCallback(Object.assign({}, latestConsent));
      }
      lastAppliedConsentKey = key;
      return true;
    }

    window.dataLayer = window.dataLayer || [];
    window.gtag =
      window.gtag ||
      function gtag() {
        window.dataLayer.push(arguments);
      };

    window.gtag('consent', 'update', mergedConsent);
    lastAppliedConsentKey = key;
    return true;
  }

  function applyFromMirrorCookie() {
    const mirror = readMirrorCookie();
    if (
      !mirror ||
      typeof mirror.analytics !== 'boolean' ||
      typeof mirror.marketing !== 'boolean'
    ) {
      return false;
    }

    // Older code also wrote false/false before a visitor made any choice.
    // Consume that ambiguous mirror without overriding the default wait or
    // falling back to a possibly stale legacy grant. Shopify must confirm it.
    if (isUnconfirmedDenial(mirror)) {
      return true;
    }

    return applyGoogleConsentState(buildConsentState(mirror.analytics, mirror.marketing));
  }

  function isUnconfirmedDenial(mirror) {
    return mirror && mirror.analytics === false && mirror.marketing === false &&
      mirror.choice_confirmed !== true;
  }

  function applyFromLegacyCookie() {
    const categories = readLegacyCookie();
    if (categories === null) {
      return false;
    }

    const consent = consentFromLegacyCategories(categories);
    writeMirrorCookie(
      consent.analytics_storage === GRANTED,
      consent.ad_storage === GRANTED
    );
    return applyGoogleConsentState(consent);
  }

  function applyStoredConsent() {
    if (applyFromMirrorCookie()) {
      return true;
    }

    return applyFromLegacyCookie();
  }

  function applyInternalTrafficConsentBlock() {
    if (
      !window.VecickyGtmInternalTraffic ||
      typeof window.VecickyGtmInternalTraffic.isActive !== 'function' ||
      !window.VecickyGtmInternalTraffic.isActive()
    ) {
      return false;
    }

    applyGoogleConsentState(buildConsentState(false, false));
    // This return value means "internal visitor blocked", not "state changed".
    // A duplicate denied update must still stop a later banner grant.
    return true;
  }

  function hasPersistedConsent() {
    const mirror = readMirrorCookie();
    if (isUnconfirmedDenial(mirror)) {
      return false;
    }
    if (
      mirror &&
      typeof mirror.analytics === 'boolean' &&
      typeof mirror.marketing === 'boolean'
    ) {
      return true;
    }

    return readLegacyCookie() !== null;
  }

  /**
   * Map Shopify Customer Privacy API state to Google Consent Mode v2.
   * Supports:
   * - getTrackingConsent() shorthand: "yes" | "no" | "no_interaction"
   * - currentVisitorConsent(): { analytics, marketing, ... }
   * - visitorConsentCollected detail: { analyticsAllowed, marketingAllowed, ... }
   */
  function consentStateFromShopify(tracking) {
    if (tracking == null || tracking === '') {
      return null;
    }

    if (typeof tracking === 'string') {
      if (tracking === 'yes') {
        return buildConsentState(true, true);
      }
      if (tracking === 'no') {
        return buildConsentState(false, false);
      }
      return null;
    }

    if (typeof tracking !== 'object') {
      return null;
    }

    // Shopify '' means no decision, NOT an explicit denial. Sending an update
    // here would prematurely release Google's wait_for_update window and store
    // a false choice in our mirror cookie.
    const analyticsAllowed = typeof tracking.analyticsAllowed === 'boolean'
      ? tracking.analyticsAllowed
      : tracking.analytics === 'yes' ? true : tracking.analytics === 'no' ? false : null;
    const marketingAllowed = typeof tracking.marketingAllowed === 'boolean'
      ? tracking.marketingAllowed
      : tracking.marketing === 'yes' ? true : tracking.marketing === 'no' ? false : null;
    if (analyticsAllowed === null && marketingAllowed === null) {
      return null;
    }

    return buildConsentState(analyticsAllowed, marketingAllowed);
  }

  function applyGoogleConsent(tracking) {
    const consent = consentStateFromShopify(tracking);
    if (!consent) {
      return false;
    }

    const changed = applyGoogleConsentState(consent);
    // Preserve the existing live integration: tags waiting for a real banner
    // grant listen to this event after their initial All Pages trigger.
    if (changed && (consent.analytics_storage === GRANTED || consent.ad_storage === GRANTED)) {
      window.dataLayer.push({ event: 'cookie_consent_granted' });
    }
    // The legacy mirror format can express only a complete boolean choice.
    // Never persist an undecided category as false.
    if (latestConsent && latestConsent.analytics_storage && latestConsent.ad_storage) {
      writeMirrorCookie(
        latestConsent.analytics_storage === GRANTED,
        latestConsent.ad_storage === GRANTED
      );
    }
    return changed;
  }

  function readInitialConsent(customerPrivacy) {
    if (applyInternalTrafficConsentBlock()) {
      return;
    }

    if (hasPersistedConsent()) {
      return;
    }

    if (typeof customerPrivacy.currentVisitorConsent === 'function') {
      applyGoogleConsent(customerPrivacy.currentVisitorConsent());
      return;
    }

    const result = customerPrivacy.getTrackingConsent();
    if (result && typeof result.then === 'function') {
      result.then(applyGoogleConsent).catch(function () {});
      return;
    }
    applyGoogleConsent(result);
  }

  function initShopifyConsentApi() {
    window.Shopify = window.Shopify || {};
    if (typeof window.Shopify.loadFeatures !== 'function') {
      return false;
    }

    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      function (error) {
        if (error || !window.Shopify.customerPrivacy) {
          return;
        }

        readInitialConsent(window.Shopify.customerPrivacy);
      }
    );

    return true;
  }

  function scheduleShopifyConsentInit(attempt) {
    if (initShopifyConsentApi()) {
      return;
    }

    if (attempt >= SHOPIFY_INIT_MAX_ATTEMPTS) {
      return;
    }

    window.setTimeout(function () {
      scheduleShopifyConsentInit(attempt + 1);
    }, SHOPIFY_INIT_RETRY_MS);
  }

  document.addEventListener('visitorConsentCollected', function (event) {
    if (applyInternalTrafficConsentBlock()) {
      return;
    }

    applyGoogleConsent(event.detail);
  });

  applyStoredConsent();
  applyInternalTrafficConsentBlock();
  scheduleShopifyConsentInit(0);

  window.VecickyGtmConsent = {
    registerNativeConsent: registerNativeConsent,
    MIRROR_COOKIE_NAME: MIRROR_COOKIE_NAME,
    LEGACY_COOKIE_NAME: LEGACY_COOKIE_NAME,
    buildConsentState: buildConsentState,
    consentStateFromShopify: consentStateFromShopify,
    consentFromLegacyCategories: consentFromLegacyCategories,
    readMirrorCookie: readMirrorCookie,
    readLegacyCookie: readLegacyCookie,
    writeMirrorCookie: writeMirrorCookie,
    applyFromMirrorCookie: applyFromMirrorCookie,
    applyFromLegacyCookie: applyFromLegacyCookie,
    applyStoredConsent: applyStoredConsent,
    hasPersistedConsent: hasPersistedConsent,
    applyInternalTrafficConsentBlock: applyInternalTrafficConsentBlock,
  };
})();
